package fr.manyverse.app.presentation.components.modules.digicode

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Backspace
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Minimize
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.DigicodeStatus
import fr.manyverse.app.presentation.theme.success
import kotlinx.coroutines.delay
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.confirm
import manyverse.shared.generated.resources.digicode_attempts_remaining
import manyverse.shared.generated.resources.digicode_backspace
import manyverse.shared.generated.resources.digicode_close
import manyverse.shared.generated.resources.digicode_failure_message
import manyverse.shared.generated.resources.digicode_minimize
import manyverse.shared.generated.resources.digicode_quit
import manyverse.shared.generated.resources.digicode_success_message
import manyverse.shared.generated.resources.digicode_timeout_message
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock

/**
 * Fullscreen overlay shown to the player when a digicode challenge is active.
 * Displays a numeric keypad, digit indicators, countdown, and result messages.
 */
@Composable
fun DigicodePlayerOverlay(
    digicode: ActiveDigicode,
    onSubmit: (String) -> Unit,
    onTimeout: () -> Unit,
    onClose: () -> Unit,
    onMinimize: () -> Unit,
    isSubmitting: Boolean,
    submitError: String?,
    modifier: Modifier = Modifier
) {
    var currentInput by remember(digicode.id) { mutableStateOf("") }

    // Reset input on each new attempt (when attempt count changes after a failure)
    LaunchedEffect(digicode.attempts.size) {
        val lastAttempt = digicode.attempts.lastOrNull()
        if (lastAttempt != null && !lastAttempt.isSuccess && digicode.status == DigicodeStatus.ACTIVE) {
            currentInput = ""
        }
    }

    // Countdown timer
    val timeLimitSeconds = digicode.timeLimitSeconds
    var remainingSeconds by remember(digicode.id) {
        val initial = if (timeLimitSeconds != null) {
            val elapsed = (Clock.System.now() - digicode.createdAt).inWholeSeconds.toInt()
            (timeLimitSeconds - elapsed).coerceAtLeast(0)
        } else Int.MAX_VALUE
        mutableIntStateOf(initial)
    }

    LaunchedEffect(digicode.id) {
        if (timeLimitSeconds != null && digicode.status == DigicodeStatus.ACTIVE) {
            while (remainingSeconds > 0) {
                delay(1000)
                val elapsed = (Clock.System.now() - digicode.createdAt).inWholeSeconds.toInt()
                remainingSeconds = (timeLimitSeconds - elapsed).coerceAtLeast(0)
                if (remainingSeconds <= 0) {
                    onTimeout()
                    break
                }
            }
        }
    }

    val isActive = digicode.status == DigicodeStatus.ACTIVE
    val canInput = isActive && !isSubmitting
    val canSubmit = canInput && currentInput.length == digicode.digitCount
    val minimizeAlignment = Alignment.TopEnd

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.85f)),
        contentAlignment = Alignment.Center
    ) {
        Surface(
            modifier = Modifier
                .safeDrawingPadding()
                .fillMaxWidth(0.9f)
                .padding(16.dp),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.surface,
            shadowElevation = 8.dp
        ) {
            Column(
                modifier = Modifier.padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                // Countdown (if time limit enabled and still active)
                if (timeLimitSeconds != null && isActive && remainingSeconds < Int.MAX_VALUE) {
                    val isUrgent = remainingSeconds <= 10
                    Text(
                        text = formatCountdown(remainingSeconds),
                        style = MaterialTheme.typography.headlineMedium,
                        color = if (isUrgent) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold
                    )
                }

                // Status message (non-ACTIVE states)
                when (digicode.status) {
                    DigicodeStatus.SUCCESS -> {
                        Text(
                            text = stringResource(Res.string.digicode_success_message),
                            style = MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.success,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    DigicodeStatus.FAILED -> {
                        Text(
                            text = stringResource(Res.string.digicode_failure_message),
                            style = MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.error,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    DigicodeStatus.TIMEOUT -> {
                        Text(
                            text = stringResource(Res.string.digicode_timeout_message),
                            style = MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.error,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    else -> {}
                }

                // Digit indicators
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    repeat(digicode.digitCount) { index ->
                        val isFilled = index < currentInput.length
                        Box(
                            modifier = Modifier
                                .size(40.dp)
                                .border(
                                    width = 2.dp,
                                    color = when {
                                        !isActive -> MaterialTheme.colorScheme.outline
                                        isFilled -> MaterialTheme.colorScheme.primary
                                        else -> MaterialTheme.colorScheme.outlineVariant
                                    },
                                    shape = CircleShape
                                )
                                .background(
                                    color = if (isFilled) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
                                    shape = CircleShape
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            if (isFilled) {
                                Text(
                                    text = "•",
                                    style = MaterialTheme.typography.titleLarge,
                                    color = MaterialTheme.colorScheme.onPrimaryContainer
                                )
                            }
                        }
                    }
                }

                // Attempts remaining (if enabled)
                if (digicode.showAttemptsToPlayer && isActive) {
                    val remaining = digicode.maxAttempts - digicode.attempts.size
                    Text(
                        text = stringResource(Res.string.digicode_attempts_remaining, remaining),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                // Submit error
                if (submitError != null && isActive) {
                    Text(
                        text = submitError,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.error,
                        textAlign = TextAlign.Center
                    )
                }

                // Numpad (only shown when ACTIVE)
                if (isActive) {
                    Numpad(
                        onDigit = { digit ->
                            if (currentInput.length < digicode.digitCount) {
                                currentInput += digit
                            }
                        },
                        onBackspace = {
                            if (currentInput.isNotEmpty()) {
                                currentInput = currentInput.dropLast(1)
                            }
                        },
                        onSubmit = {
                            if (canSubmit) {
                                onSubmit(currentInput)
                            }
                        },
                        canSubmit = canSubmit,
                        enabled = canInput
                    )
                }

                Spacer(modifier = Modifier.height(4.dp))

            }
        }

        // Close button at the bottom for all terminal states
        if (!isActive) {
            val buttonText = when (digicode.status) {
                DigicodeStatus.SUCCESS -> stringResource(Res.string.digicode_close)
                else -> stringResource(Res.string.digicode_quit)
            }
            Button(
                onClick = onClose,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .safeDrawingPadding()
                    .padding(bottom = 96.dp)
                    .width(200.dp)
            ) {
                Text(buttonText)
            }
        }

        // Minimize button (only when ACTIVE, top-right on desktop/web, bottom-right on mobile)
        if (isActive) {
            FilledIconButton(
                onClick = onMinimize,
                modifier = Modifier
                    .align(minimizeAlignment)
                    .safeDrawingPadding()
                    .padding(16.dp)
                    .size(48.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Minimize,
                    contentDescription = stringResource(Res.string.digicode_minimize)
                )
            }
        }
    }
}

@Composable
private fun Numpad(
    onDigit: (String) -> Unit,
    onBackspace: () -> Unit,
    onSubmit: () -> Unit,
    canSubmit: Boolean,
    enabled: Boolean,
    modifier: Modifier = Modifier
) {
    val rows = listOf(
        listOf("7", "8", "9"),
        listOf("4", "5", "6"),
        listOf("1", "2", "3"),
        listOf("⌫", "0", "OK")
    )

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        rows.forEach { row ->
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                row.forEach { key ->
                    when (key) {
                        "⌫" -> {
                            FilledTonalButton(
                                onClick = onBackspace,
                                enabled = enabled,
                                modifier = Modifier.size(72.dp, 56.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.Backspace,
                                    contentDescription = stringResource(Res.string.digicode_backspace)
                                )
                            }
                        }
                        "OK" -> {
                            Button(
                                onClick = onSubmit,
                                enabled = enabled && canSubmit,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.primary
                                ),
                                modifier = Modifier.size(72.dp, 56.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Check,
                                    contentDescription = stringResource(Res.string.confirm)
                                )
                            }
                        }
                        else -> {
                            OutlinedButton(
                                onClick = { onDigit(key) },
                                enabled = enabled,
                                modifier = Modifier.size(72.dp, 56.dp)
                            ) {
                                Text(
                                    text = key,
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun formatCountdown(totalSeconds: Int): String {
    val minutes = (totalSeconds / 60).toString().padStart(2, '0')
    val seconds = (totalSeconds % 60).toString().padStart(2, '0')
    return "$minutes:$seconds"
}
